import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaUsers, FaPlus, FaTrash, FaEdit, FaReceipt, FaEllipsisV, FaWhatsapp, FaPlusCircle, FaMoneyBillWave, FaCheckCircle, FaClock, FaFileExport } from 'react-icons/fa';
import './Team.scss';
import Modal from '../components/Modal/Modal';
import CustomSelect from '../components/CustomSelect/CustomSelect';
import PageHero from '../components/PageHero/PageHero';
import { useLanguage } from '../context/LanguageContext';

import { storage } from '../utils/storage';
import useConfirm from '../hooks/useConfirm';
import { STORAGE_KEYS, API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import { showToast } from '../utils/toast';
import logger from '../utils/logger';
import { buildTeamInviteWhatsApp, buildTeamInviteEmail } from '../utils/shareTemplates';

const initialMembers = storage.getJSON(STORAGE_KEYS.TEAM, []);
const TEAM_CHANGED_EVENT = 'photoflow:team-changed';

const emptyMember = { 
  name: '', 
  role: '', 
  phone: '', 
  email: '', 
  avatar: null,
  payments: [] // Array of { eventId, eventName, amount, date, status, notes }
};

// Ensure every payment has an `id` so React list keys are stable and
// operations (delete/update) reliably find the payment.
const normalizeMemberPayments = (members) => {
  if (!Array.isArray(members)) return [];
  return members.map((m) => ({
    ...m,
    payments: Array.isArray(m.payments)
      ? m.payments.map((p) => ({
          ...p,
          id: p.id || `pay-${Date.now()}-${Math.random().toString(36).slice(2,8)}`
        }))
      : []
  }));
};

// Business identifier for an event (custom EV id preferred, fallback to mongo _id)
const getTeamEventId = (ev) => String(ev?.id ?? ev?.quotationId ?? ev?._id ?? '');

// Normalize a team entry (string or object) into comparable tokens
const normalizeTeamEntry = (entry) => {
  if (entry == null) return {};
  if (typeof entry === 'string' || typeof entry === 'number') {
    const s = String(entry).trim();
    return { name: s.toLowerCase(), id: s, email: s.toLowerCase() };
  }
  if (typeof entry === 'object') {
    const name = String(entry.name ?? entry.memberName ?? entry.label ?? '').trim().toLowerCase();
    const id = entry._id != null ? String(entry._id) : (entry.id != null ? String(entry.id) : '');
    const email = String(entry.email ?? '').trim().toLowerCase();
    const phone = String(entry.phone ?? '').replace(/[^0-9]/g, '');
    return { name, id, email, phone };
  }
  return {};
};

// True only when the event is explicitly assigned to the member.
// Unassigned events (empty team) always return false and are hidden.
const isEventAssignedToMember = (ev, member) => {
  if (!ev || !member) return false;
  const memberName = String(member?.name ?? '').trim().toLowerCase();
  const memberId = member?._id != null ? String(member._id) : '';
  const memberEmail = String(member?.email ?? '').trim().toLowerCase();
  const memberPhone = String(member?.phone ?? '').replace(/[^0-9]/g, '');

  const candidates = [];
  if (Array.isArray(ev.team)) candidates.push(...ev.team);
  else if (typeof ev.team === 'string' && ev.team.trim()) candidates.push(...ev.team.split(','));
  if (Array.isArray(ev.teamIds)) candidates.push(...ev.teamIds);
  if (Array.isArray(ev.memberIds)) candidates.push(...ev.memberIds);
  if (Array.isArray(ev.teamMembers)) candidates.push(...ev.teamMembers);
  if (Array.isArray(ev.members)) candidates.push(...ev.members);

  if (candidates.length === 0) return false;

  return candidates.some((entry) => {
    const n = normalizeTeamEntry(entry);
    if (!n.name && !n.id && !n.email && !n.phone) return false;
    if (memberName && n.name && n.name === memberName) return true;
    if (memberId && n.id && String(n.id) === String(memberId)) return true;
    // team entries that are plain ids matching the member id
    if (memberId && n.name && n.name === String(memberId).toLowerCase()) return true;
    if (memberEmail && n.email && n.email === memberEmail) return true;
    if (memberPhone && n.phone && n.phone === memberPhone) return true;
    return false;
  });
};

const getAssignedEventsForMember = (member, eventsList) => {
  if (!member || !Array.isArray(eventsList)) return [];
  return eventsList.filter((ev) => isEventAssignedToMember(ev, member));
};

const Team = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [teamMembers, setTeamMembers] = useState(initialMembers);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm] = useState(emptyMember);
  const [errors, setErrors] = useState({});
  const confirm = useConfirm();

  const loadTeamMembers = useCallback(async () => {
    try {
      const token = session.getToken();
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        setTeamMembers(normalizeMemberPayments(data));
      }
    } catch (e) {
      // Keep the current optimistic state if the refresh fails.
    }
  }, []);
  
  // Get current logged-in user from Redux
  const authUser = useSelector((state) => state.auth.user);
  const currentUserId = authUser?.id || authUser?._id || authUser?.email || 'anonymous';
  const currentUserEmail = authUser?.email || session.getUser()?.email || '';
  const currentUserName = authUser?.name || session.getUser()?.name || authUser?.email || 'You';
  const currentUserPhone = authUser?.phone || session.getUser()?.phone || '';
  // Invite modal for newly created non-Mivent members
  const [inviteMember, setInviteMember] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedMemberForPayment, setSelectedMemberForPayment] = useState(null);
  const [useCustomEventName, setUseCustomEventName] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    eventId: '',
    eventName: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    status: 'pending', // pending, paid, partial
    notes: ''
  });
  const [paymentErrors, setPaymentErrors] = useState({});
  // Payment being edited (null = add mode)
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  
  // Load events for payment dropdown
  const [events, setEvents] = useState([]);
  // Events filtered for the selected member in payment modal
  const [eventsForMember, setEventsForMember] = useState([]);
  
  // Pagination for payments table
  const [paymentsPage, setPaymentsPage] = useState(1);
  const itemsPerPage = 10;

  // Helper to format dates for display as dd MMM yyyy
  const formatDate = (dateInput) => {
    if (!dateInput && dateInput !== 0) return '—';
    try {
      const s = String(dateInput);
      // If already in yyyy-mm-dd (from input[type=date]) parse and reformat
      const isoMatch = s.match(/^\d{4}-\d{1,2}-\d{1,2}$/);
      if (isoMatch) {
        const [y, m, d] = s.split('-');
        const dt = new Date(Number(y), Number(m) - 1, Number(d));
        return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
      const dt = new Date(dateInput);
      if (!isFinite(dt.getTime())) return s;
      return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return String(dateInput);
    }
  };

  // Escape regex special chars
  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&');

  // Highlight occurrences of `query` inside `text` (case-insensitive)
  // Returns a mix of strings and <span className="search-highlight">matched</span>
  const highlightText = (text, query) => {
    const q = String(query || '').trim();
    if (!q) return text;
    const str = String(text == null ? '' : text);
    if (!str) return str;
    try {
      const splitRe = new RegExp(`(${escapeRegExp(q)})`, 'ig');
      const testRe = new RegExp(`^${escapeRegExp(q)}$`, 'i');
      const parts = str.split(splitRe);
      return parts.map((part, idx) => (
        testRe.test(part) ? <span key={idx} className="search-highlight">{part}</span> : part
      ));
    } catch (e) {
      return str;
    }
  };
  
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const token = session.getToken();
        if (!token) {
          setEvents([]);
          return;
        }
        const res = await fetch(`${API_BASE_URL}/events`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          setEvents([]);
          return;
        }
        const data = await res.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        setEvents([]);
      }
    };
    loadEvents();
  }, []);

  useEffect(() => {
    const handleEventsChanged = () => {
      const reloadEvents = async () => {
        try {
          const token = session.getToken();
          if (!token) {
            setEvents([]);
            return;
          }
          const res = await fetch(`${API_BASE_URL}/events`, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) {
            setEvents([]);
            return;
          }
          const data = await res.json();
          setEvents(Array.isArray(data) ? data : []);
        } catch (e) {
          setEvents([]);
        }
      };

      reloadEvents();
    };

    window.addEventListener('photoflow:events-changed', handleEventsChanged);
    return () => window.removeEventListener('photoflow:events-changed', handleEventsChanged);
  }, []);

  useEffect(() => {
    const handleTeamChanged = () => {
      void loadTeamMembers();
    };

    window.addEventListener(TEAM_CHANGED_EVENT, handleTeamChanged);
    return () => window.removeEventListener(TEAM_CHANGED_EVENT, handleTeamChanged);
  }, [loadTeamMembers]);

  // Load team members from server when authenticated; fall back to local storage
  useEffect(() => {
    const load = async () => {
      try {
        const token = session.getToken();
        if (!token) return; // unauthenticated
        const res = await fetch(`${API_BASE_URL}/team`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) {
          setTeamMembers(normalizeMemberPayments(data));
          return;
        }
      } catch (e) {
        // ignore and fall back to local
      }
      try {
        const local = storage.getJSON(STORAGE_KEYS.TEAM, []);
        setTeamMembers(Array.isArray(local) ? normalizeMemberPayments(local) : []);
      } catch (e) {
        setTeamMembers([]);
      }
    };
    load();
  }, []);

  // Payments viewer modal
  const [isPaymentsViewOpen, setIsPaymentsViewOpen] = useState(false);
  const [paymentsViewerMember, setPaymentsViewerMember] = useState(null);
  // Search for payments viewer
  const [paymentsSearch, setPaymentsSearch] = useState('');
  // Search for team list (name / role / phone)
  const [searchQuery, setSearchQuery] = useState('');
  // Track which card's menu is open
  const [openMenuId, setOpenMenuId] = useState(null);

  // Toggle card menu (stop propagation so card click doesn't open payments)
  const toggleMenu = (e, memberId) => {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === memberId ? null : memberId));
  };

  // Close menu when clicking outside
  useEffect(() => {
    if (!openMenuId) return undefined;
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  // Open WhatsApp with a cleaned phone number (uses wa.me)
  const openWhatsApp = (e, phone) => {
    e.stopPropagation();
    if (!phone) {
      showToast('No phone number available', 'error');
      return;
    }
    const clean = String(phone).replace(/[^0-9]/g, '');
    if (!clean) {
      showToast('Invalid phone number', 'error');
      return;
    }
    try {
      window.open(`https://wa.me/${clean}`, '_blank');
    } catch (err) {
      // Fallback: copy number to clipboard
      try { navigator.clipboard.writeText(clean); showToast('Phone copied to clipboard', 'info'); } catch (_) {}
    }
  };

  const openAdd = () => {
    setForm(emptyMember);
    setEditingMember(null);
    setErrors({});
    setIsModalOpen(true);
  };

  const location = useLocation();
  const pendingTeamIdRef = useRef(null);

  useEffect(() => {
    if (location?.state?.openAdd) {
      openAdd();
      navigate('/team', { replace: true, state: {} });
    }
    // Deep-link for push clicks when app was closed (SW opens /team?id=...)
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const member = teamMembers.find(m => String(m._id) === String(qId));
        if (member) {
          // Highlight and open payments view to show related content even without app open
          openPaymentsForMember(member);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else if (qId) {
          // Member not yet loaded — keep id for retry when teamMembers loads
          pendingTeamIdRef.current = String(qId);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pending deep-link retry for team members loaded async
  useEffect(() => {
    if (pendingTeamIdRef.current && teamMembers.length) {
      const member = teamMembers.find(m => String(m._id) === String(pendingTeamIdRef.current));
      if (member) {
        openPaymentsForMember(member);
        pendingTeamIdRef.current = null;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } catch (e) {}
      }
    }
  }, [teamMembers]);

  // Handle push deep-link when app already open (SW client.navigate)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const member = teamMembers.find(m => String(m._id) === String(qId));
        if (member) {
          openPaymentsForMember(member);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Also listen for in-app bell-panel notifications (NotificationPanel dispatch)
  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/team') return;
        const state = detail.state || {};
        if (state.openViewId) {
          const member = teamMembers.find(m => String(m._id) === String(state.openViewId));
          if (member) openPaymentsForMember(member);
          else pendingTeamIdRef.current = String(state.openViewId);
        }
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [teamMembers]);

  useEffect(() => {
    const openPaymentTarget = location?.state?.openPayment;
    if (!openPaymentTarget || isPaymentModalOpen) return;

    const targetName = String(openPaymentTarget?.name || openPaymentTarget || '').trim().toLowerCase();
    if (!targetName) return;

    const matchedMember = (teamMembers || []).find((member) => String(member?.name || '').trim().toLowerCase() === targetName);
    if (matchedMember) {
      openPaymentModal(matchedMember);
      navigate('/team', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.openPayment, teamMembers, isPaymentModalOpen]);

  const openEdit = (member) => {
    setForm({ ...member });
    setEditingMember(member._id);
    setErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setForm((prev) => ({ ...prev, [name]: (value || '').replace(/\D/g, '').slice(0, 10) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const err = {};
    if (!form.name || !form.name.trim()) err.name = 'Name is required';
    if (form.phone && !/^\d{10}$/.test((form.phone || '').replace(/\D/g, ''))) err.phone = 'Enter a valid 10-digit phone number (numbers only)';
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) err.email = 'Enter a valid email address';
    return err;
  };

  const saveMember = () => {
    const validation = validate();
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    const payload = {
      ...form,
      name: String(form.name || '').trim(),
      role: String(form.role || '').trim(),
      phone: String(form.phone || '').replace(/\D/g, '').slice(0, 10),
      email: String(form.email || '').trim().toLowerCase(),
    };

    if (editingMember) {
      // Optimistic update
      const updatedMembers = teamMembers.map((m) => (m._id === editingMember ? { ...m, ...payload } : m));
      setTeamMembers(updatedMembers);
      // Persist to server
      (async () => {
        try {
          const token = session.getToken();
          if (!token) return;
          const res = await fetch(`${API_BASE_URL}/team/${encodeURIComponent(editingMember)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...payload, _id: editingMember }),
          });
          if (res.ok) {
            const updated = await res.json();
            setTeamMembers((prev) => prev.map((m) => (m._id === editingMember ? updated : m)));
            window.dispatchEvent(new CustomEvent(TEAM_CHANGED_EVENT));
            // Name change cascades to events on backend (team.js PUT), refresh events list so assignment stays in sync
            try { window.dispatchEvent(new CustomEvent('photoflow:events-changed')); } catch (e) {}
            showToast('Team member updated successfully', { type: 'success' });
          } else {
            // Revert on error and show error message
            const errorData = await res.json().catch(() => ({ error: 'Failed to update team member' }));
            setTeamMembers(teamMembers);
            showToast(errorData.error || 'Failed to update team member', 'error');
            logger.error('Failed to update team member', errorData, 'Team');
          }
        } catch (e) {
          // Revert on error
          setTeamMembers(teamMembers);
          logger.error('Error updating team member', e, 'Team');
        }
      })();
    } else {
      // Add new member
      const tempId = `temp-${Date.now()}`; // Temporary ID for optimistic update
      const newMember = { ...payload, _id: tempId };
      const newMembers = [newMember, ...teamMembers];
      setTeamMembers(newMembers);
      (async () => {
        try {
          const token = session.getToken();
          logger.debug("Token from session retrieved", null, 'Team');
          if (!token) {
            setTeamMembers(teamMembers); // Revert
            logger.warn("No token available, cannot create team member", null, 'Team');
            return;
          }
          // Don't send _id to backend; let the backend generate it
          const { _id, ...memberData } = payload;
          logger.debug("Sending member data to backend", memberData, 'Team');
          const res = await fetch(`${API_BASE_URL}/team`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(memberData),
          });
          logger.debug(`Response status: ${res.status}`, null, 'Team');
          if (res.ok) {
            const created = await res.json();
            logger.debug("Team member created successfully", created, 'Team');
            setTeamMembers((prev) => prev.map((m) => (m._id === tempId ? created : m)));
            window.dispatchEvent(new CustomEvent(TEAM_CHANGED_EVENT));
            try { window.dispatchEvent(new CustomEvent('notifications:refresh')); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),600); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),1600);} catch(e){}
            // If new member is NOT a Mivent user, offer polished WhatsApp/Email welcome invite
            if (created && created.isMiventUser === false) {
              setInviteMember(created);
              setShowInviteModal(true);
              showToast(`${created.name || 'Member'} is not on Mivent — send a welcome invite`, { type: 'warning', duration: 5000 });
            } else {
              showToast('Team member added successfully', { type: 'success' });
            }
          } else {
            const errorData = await res.json().catch(() => ({ error: 'Failed to create team member' }));
            logger.error(`Failed to create team member. Status: ${res.status}`, errorData, 'Team');
            // Revert on error and show error to user
            setTeamMembers(teamMembers);
            showToast(errorData.error || 'Failed to create team member', 'error');
          }
        } catch (e) {
          // Revert on error
          setTeamMembers(teamMembers);
          logger.error('Error creating team member', e, 'Team');
        }
      })();
    }

    setIsModalOpen(false);
  };

  const deleteMember = async (id) => {
    const ok = await confirm('Delete this team member?');
    if (!ok) return;
    const memberToDelete = teamMembers.find(m => m._id === id);
    setTeamMembers((prev) => prev.filter((m) => m._id !== id));
    // Fire-and-forget server delete
    (async () => {
      try {
        const token = session.getToken();
        if (!token) {
          // Revert on error
          setTeamMembers((prev) => [...prev, memberToDelete]);
          return;
        }
        const res = await fetch(`${API_BASE_URL}/team/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          // Revert on error
          setTeamMembers((prev) => [...prev, memberToDelete]);
          console.error('Failed to delete team member');
        } else {
          window.dispatchEvent(new CustomEvent(TEAM_CHANGED_EVENT));
        }
      } catch (e) {
        // Revert on error
        setTeamMembers((prev) => [...prev, memberToDelete]);
        console.error('Error deleting team member:', e);
      }
    })();
  };
  
  // Payment management functions
  const openPaymentModal = (member) => {
    setSelectedMemberForPayment(member);
    setUseCustomEventName(false);
    setPaymentForm({
      eventId: '',
      eventName: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      status: 'pending',
      notes: ''
    });
    setPaymentErrors({});
    setEditingPaymentId(null);
    // Only events explicitly assigned to this member — never unassigned events
    try {
      setEventsForMember(getAssignedEventsForMember(member, events));
    } catch (e) {
      setEventsForMember([]);
    }

    setIsPaymentModalOpen(true);
  };

  const openEditPaymentModal = (member, payment) => {
    setSelectedMemberForPayment(member);
    setEditingPaymentId(payment.id);
    // Only events assigned to this member. Keep the payment's current event
    // visible even if the member was unassigned since, so edit doesn't lose data.
    try {
      const assigned = getAssignedEventsForMember(member, events);
      const currentId = payment?.eventId != null ? String(payment.eventId) : '';
      const hasCurrent = currentId && assigned.some((ev) => getTeamEventId(ev) === currentId);
      if (currentId && !hasCurrent) {
        const currentEvent = (events || []).find((ev) => getTeamEventId(ev) === currentId);
        setEventsForMember(currentEvent ? [...assigned, currentEvent] : assigned);
      } else {
        setEventsForMember(assigned);
      }
    } catch (e) {
      setEventsForMember([]);
    }
    // If the payment references an event from the list, use the dropdown mode, otherwise the custom name input
    const hasEventId = !!payment.eventId && events.some((ev) => getTeamEventId(ev) === String(payment.eventId));
    setUseCustomEventName(!hasEventId);
    const rawDate = payment.date ? String(payment.date) : '';
    let dateValue = new Date().toISOString().split('T')[0];
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(rawDate)) {
      dateValue = rawDate;
    } else {
      const dt = new Date(rawDate);
      if (isFinite(dt.getTime())) {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const d = String(dt.getDate()).padStart(2, '0');
        dateValue = `${y}-${m}-${d}`;
      }
    }
    setPaymentForm({
      eventId: payment.eventId || '',
      eventName: payment.eventName || '',
      amount: payment.amount ?? '',
      date: dateValue,
      status: payment.status || 'pending',
      notes: payment.notes || ''
    });
    setPaymentErrors({});
    setIsPaymentModalOpen(true);
  };

  useEffect(() => {
    if (!isPaymentModalOpen || !selectedMemberForPayment) return;

    try {
      const assigned = getAssignedEventsForMember(selectedMemberForPayment, events);
      // Preserve the event currently selected in the form (edit case) if present
      const currentId = paymentForm?.eventId != null && String(paymentForm.eventId) !== ''
        ? String(paymentForm.eventId)
        : '';
      if (currentId && !assigned.some((ev) => getTeamEventId(ev) === currentId)) {
        const currentEvent = (events || []).find((ev) => getTeamEventId(ev) === currentId);
        setEventsForMember(currentEvent ? [...assigned, currentEvent] : assigned);
      } else {
        setEventsForMember(assigned);
      }
    } catch (e) {
      setEventsForMember([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, isPaymentModalOpen, selectedMemberForPayment]);

  const openPaymentsForMember = (member) => {
    setPaymentsViewerMember(member);
    setIsPaymentsViewOpen(true);
  };

  const closePaymentsViewer = () => {
    setIsPaymentsViewOpen(false);
    setPaymentsViewerMember(null);
    setPaymentsPage(1);
    setPaymentsSearch('');
  };

  const handlePaymentsSearch = (e) => {
    setPaymentsSearch(e.target.value || '');
    setPaymentsPage(1);
  };
  
  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedMemberForPayment(null);
    setEditingPaymentId(null);
    setUseCustomEventName(false);
    navigate('/team', { replace: true, state: {} });
  };
  
  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));

    // Auto-fill event name when event is selected (only if not using custom name)
    // Resolve from the member-assigned list first so unassigned events can't leak in
    if (name === 'eventId' && value && !useCustomEventName) {
      // Compare IDs as strings to tolerate numbers vs strings in storage
      const selectedEvent =
        (eventsForMember || []).find((ev) => getTeamEventId(ev) === String(value)) ||
        (events || []).find((ev) => getTeamEventId(ev) === String(value));
      if (selectedEvent) {
        setPaymentForm((prev) => ({
          ...prev,
          eventName: selectedEvent.clientName || `Event ${selectedEvent.quotationId || getTeamEventId(selectedEvent)}`
        }));
      }
    }
  };

  const handlePaymentEventChange = (eventId) => {
    if (!useCustomEventName && eventId) {
      const selectedEvent =
        (eventsForMember || []).find((ev) => getTeamEventId(ev) === String(eventId)) ||
        (events || []).find((ev) => getTeamEventId(ev) === String(eventId));
      if (selectedEvent) {
        // Guard: never accept an event that isn't assigned to this member
        if (selectedMemberForPayment && !isEventAssignedToMember(selectedEvent, selectedMemberForPayment)) {
          setPaymentErrors((prev) => ({ ...prev, eventId: 'This event is not assigned to the selected member' }));
          return;
        }
        setPaymentForm((prev) => ({
          ...prev,
          eventId,
          eventName: selectedEvent.clientName || `Event ${selectedEvent.quotationId || getTeamEventId(selectedEvent)}`
        }));
        setPaymentErrors((prev) => ({ ...prev, eventId: '' }));
        return;
      }
    }
    setPaymentForm((prev) => ({ ...prev, eventId }));
  };
  
  const handlePaymentStatusChange = (status) => {
    setPaymentForm((prev) => ({ ...prev, status }));
  };
  
  const validatePayment = () => {
    const err = {};
    // Require either eventId (from dropdown) OR custom eventName (free text)
    if (!useCustomEventName && !paymentForm.eventId) {
      err.eventId = 'Please select an event or use custom event name';
    }
    if (!useCustomEventName && paymentForm.eventId && selectedMemberForPayment) {
      const chosen =
        (eventsForMember || []).find((ev) => getTeamEventId(ev) === String(paymentForm.eventId)) ||
        (events || []).find((ev) => getTeamEventId(ev) === String(paymentForm.eventId));
      if (chosen && !isEventAssignedToMember(chosen, selectedMemberForPayment)) {
        err.eventId = 'This event is not assigned to the selected member';
      }
    }
    if (useCustomEventName && !paymentForm.eventName?.trim()) {
      err.eventName = 'Please enter an event name';
    }
    if (!paymentForm.amount || isNaN(paymentForm.amount) || Number(paymentForm.amount) <= 0) {
      err.amount = 'Enter a valid amount';
    }
    if (!paymentForm.date) err.date = 'Payment date is required';
    return err;
  };
  
  const savePayment = () => {
    const validation = validatePayment();
    if (Object.keys(validation).length) {
      setPaymentErrors(validation);
      return;
    }
    
    const payment = {
      // keep existing id when editing, otherwise generate a temporary unique id so React list keys are stable before server persists
      id: editingPaymentId || `pay-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
      eventId: paymentForm.eventId,
      eventName: paymentForm.eventName,
      amount: parseFloat(paymentForm.amount),
      date: paymentForm.date,
      status: paymentForm.status,
      notes: paymentForm.notes
    };
    
    const updatedMembers = teamMembers.map((m) => {
      if (m._id === selectedMemberForPayment._id) {
        const payments = Array.isArray(m.payments) ? m.payments : [];
        const nextPayments = editingPaymentId
          ? payments.map(p => p.id === editingPaymentId ? { ...p, ...payment } : p)
          : [...payments, payment];
        return { ...m, payments: nextPayments };
      }
      return m;
    });
    
  setTeamMembers(updatedMembers);
  // If payments viewer is open for this member, refresh it so the table updates instantly
  setPaymentsViewerMember((prev) => (prev && prev._id === selectedMemberForPayment?._id ? updatedMembers.find(m => m._id === selectedMemberForPayment._id) : prev));
  // Persist member payments by updating the member on server
  (async () => {
    try {
      const token = session.getToken();
      if (!token) return;
      const member = updatedMembers.find(m => m._id === selectedMemberForPayment._id);
      if (!member) return;
      const res = await fetch(`${API_BASE_URL}/team/${encodeURIComponent(member._id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(member),
      });
      if (!res.ok) {
        // Revert on error
        setTeamMembers(teamMembers);
        console.error('Failed to save payment');
      }
    } catch (e) {
      // Revert on error
      setTeamMembers(teamMembers);
      console.error('Error saving payment:', e);
    }
  })();
    setIsPaymentModalOpen(false);
  };
  
  const deletePayment = async (memberId, paymentId) => {
    const ok = await confirm('Delete this payment record?');
    if (!ok) return;
    
    const memberToUpdate = teamMembers.find(m => m._id === memberId);
    const updatedMembers = teamMembers.map((m) => {
      if (m._id === memberId) {
        const payments = (m.payments || []).filter(p => p.id !== paymentId);
        return { ...m, payments };
      }
      return m;
    });
    
  setTeamMembers(updatedMembers);
  // Refresh payments viewer if it's showing this member
  setPaymentsViewerMember((prev) => (prev && prev._id === memberId ? updatedMembers.find(m => m._id === memberId) || null : prev));
  (async () => {
    try {
      const token = session.getToken();
      if (!token) {
        // Revert on error
        setTeamMembers((prev) => prev.map(m => m._id === memberId ? memberToUpdate : m));
        return;
      }
      const member = updatedMembers.find(m => m._id === memberId);
      if (!member) return;
      const res = await fetch(`${API_BASE_URL}/team/${encodeURIComponent(memberId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(member),
      });
      if (!res.ok) {
        // Revert on error
        setTeamMembers((prev) => prev.map(m => m._id === memberId ? memberToUpdate : m));
        console.error('Failed to delete payment');
      }
    } catch (e) {
      // Revert on error
      setTeamMembers((prev) => prev.map(m => m._id === memberId ? memberToUpdate : m));
      console.error('Error deleting payment:', e);
    }
  })();
  };
  
  const updatePaymentStatus = (memberId, paymentId, newStatus) => {
    const memberToUpdate = teamMembers.find(m => m._id === memberId);
    const updatedMembers = teamMembers.map((m) => {
      if (m._id === memberId) {
        const payments = (m.payments || []).map(p => 
          p.id === paymentId ? { ...p, status: newStatus } : p
        );
        return { ...m, payments };
      }
      return m;
    });
    
  setTeamMembers(updatedMembers);
  // Refresh payments viewer if it's showing this member so status change is visible immediately
  setPaymentsViewerMember((prev) => (prev && prev._id === memberId ? updatedMembers.find(m => m._id === memberId) : prev));
  (async () => {
    try {
      const token = session.getToken();
      if (!token) {
        // Revert on error
        setTeamMembers((prev) => prev.map(m => m._id === memberId ? memberToUpdate : m));
        return;
      }
      const member = updatedMembers.find(m => m._id === memberId);
      if (!member) return;
      const res = await fetch(`${API_BASE_URL}/team/${encodeURIComponent(memberId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(member),
      });
      if (!res.ok) {
        // Revert on error
        setTeamMembers((prev) => prev.map(m => m._id === memberId ? memberToUpdate : m));
        console.error('Failed to update payment status');
      }
    } catch (e) {
      // Revert on error
      setTeamMembers((prev) => prev.map(m => m._id === memberId ? memberToUpdate : m));
      console.error('Error updating payment status:', e);
    }
  })();
  };
  
  // Calculate total payments for a member
  const getTotalPayments = (member) => {
    if (!member || !member.payments || !Array.isArray(member.payments)) return 0;
    return member.payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  };
  
  const getPaidAmount = (member) => {
    if (!member || !member.payments || !Array.isArray(member.payments)) return 0;
    return member.payments
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + (p.amount || 0), 0);
  };
  
  const getPendingAmount = (member) => {
    if (!member || !member.payments || !Array.isArray(member.payments)) return 0;
    return member.payments
      .filter(p => p.status === 'pending' || p.status === 'partial')
      .reduce((sum, p) => sum + (p.amount || 0), 0);
  };

  // Calculate global stats from all team members
  const totalMembers = teamMembers.length;
  const totalPayment = teamMembers.reduce((sum, m) => sum + getTotalPayments(m), 0);
  const totalPaid = teamMembers.reduce((sum, m) => sum + getPaidAmount(m), 0);
  const totalPending = teamMembers.reduce((sum, m) => sum + getPendingAmount(m), 0);

  // Format currency for display
  const formatCurrency = (amount) => {
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  const escapeCsvValue = (value) => {
    const text = String(value ?? '');
    if (!/[",\r\n,]/.test(text)) return text;
    return `"${text.replace(/"/g, '""')}"`;
  };

  const exportPaymentsForMember = async () => {
    const confirmed = await confirm(`Export payments for ${String(paymentsViewerMember?.name || 'member')}?`, {
      title: 'Export Payments',
      confirmText: 'Export',
      cancelText: 'Cancel',
      showWarning: false
    });
    if (!confirmed) return;
    if (!paymentsViewerMember) {
      return;
    }

    const payments = Array.isArray(paymentsViewerMember.payments)
      ? [...paymentsViewerMember.payments].reverse()
      : [];

      const rows = payments.map((payment) => {
      const matchedEvent = events.find((event) => getTeamEventId(event) === String(payment.eventId));
      const displayEventId = payment.eventId || (matchedEvent ? getTeamEventId(matchedEvent) : '');
      const displayEventName = payment.eventName || (matchedEvent ? (matchedEvent.clientName || `Event ${matchedEvent.quotationId || getTeamEventId(matchedEvent)}`) : '');

      return [
        escapeCsvValue(displayEventId),
        escapeCsvValue(displayEventName),
          // Export numeric amount without currency symbol
          escapeCsvValue(payment.amount != null ? Number(payment.amount).toLocaleString('en-IN') : ''),
        escapeCsvValue(formatDate(payment.date)),
        escapeCsvValue(payment.status || ''),
        escapeCsvValue(payment.notes || ''),
      ].join(',');
    });

    const csvContent = [
      ['Event ID', 'Event', 'Payment', 'Payment Date', 'Status', 'Notes'].join(','),
      ...rows,
    ].join('\n');

    const safeName = String(paymentsViewerMember.name || 'member')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'member';
    const fileName = `payments-${safeName}-${new Date().toISOString().slice(0, 10)}.csv`;

    try {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      showToast('Payments exported successfully', 'success');
    } catch (error) {
      logger.error('Failed to export payments', error, 'Team');
      showToast('Failed to export payments', 'error');
    }
  };

  return (
    <div className="team">
      <PageHero
        icon={<FaUsers />}
        title={t('pages.team')}
        subtitle={t('pages.teamSubtitle')}
        actions={
          <button className="page-hero__btn" onClick={openAdd}>
            <FaPlus /> {t('team.addMember')}
          </button>
        }
      />

      {/* Stats Section */}
      <div className="team__stats">
        <div className="team__stat-card">
          <div className="team__stat-icon team__stat-icon--total">
            <FaUsers />
          </div>
          <div className="team__stat-info">
            <h3>{totalMembers}</h3>
            <p>{t('team.statTotal')}</p>
          </div>
        </div>
        <div className="team__stat-card">
          <div className="team__stat-icon team__stat-icon--payment">
            <FaMoneyBillWave />
          </div>
          <div className="team__stat-info">
            <h3>{formatCurrency(totalPayment)}</h3>
            <p>{t('team.statPayment')}</p>
          </div>
        </div>
        <div className="team__stat-card">
          <div className="team__stat-icon team__stat-icon--paid">
            <FaCheckCircle />
          </div>
          <div className="team__stat-info">
            <h3>{formatCurrency(totalPaid)}</h3>
            <p>{t('team.statPaid')}</p>
          </div>
        </div>
        <div className="team__stat-card">
          <div className="team__stat-icon team__stat-icon--pending">
            <FaClock />
          </div>
          <div className="team__stat-info">
            <h3>{formatCurrency(totalPending)}</h3>
            <p>{t('team.statPending')}</p>
          </div>
        </div>
      </div>

      <div className="team__search">
        <input
          type="search"
          placeholder={t('team.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="team__grid">
        {(() => {
          const q = (searchQuery || '').trim().toLowerCase();
          const filtered = (teamMembers || []).filter((member) => {
            if (!q) return true;
            const name = String(member?.name || '').toLowerCase();
            const role = String(member?.role || '').toLowerCase();
            const phone = String(member?.phone || '').toLowerCase();
            return name.includes(q) || role.includes(q) || phone.includes(q);
          });

          if (filtered.length === 0) {
            return <div className="team__empty">{t('team.emptyTitle')}</div>;
          }

          return filtered.map((member) => (
            <div key={member._id} className="team-card" onClick={() => openPaymentsForMember(member)}>
              {/* 3-dot menu at top right */}
              <div className="team-card__menu">
                <button 
                  className="team-card__menu-button" 
                  onClick={(e) => toggleMenu(e, member._id)}
                  title="More options"
                >
                  <FaEllipsisV />
                </button>
                {openMenuId === member._id && (
                  <div className="team-card__dropdown">
                    <button 
                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); openEdit(member); }}
                      className="dropdown-item"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); deleteMember(member._id); }}
                      className="dropdown-item dropdown-item--danger"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                )}
              </div>

              <div className="team-card__avatar">
                {member.linkedUserPhotoURL ? (
                  <img
                    src={member.linkedUserPhotoURL}
                    alt={member.name}
                    className="team-card__avatar-img"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <span
                  className="team-card__avatar-initials"
                  style={member.linkedUserPhotoURL ? { display: 'none' } : {}}
                >
                  {member.name ? String(member.name).charAt(0) : '?'}
                </span>
              </div>
              <h3 className="team-card__name">{highlightText(member.name, searchQuery)}</h3>
              <p className="team-card__role">{highlightText(member.role, searchQuery)}</p>
              {member.phone && <p className="team-card__phone">{highlightText(member.phone, searchQuery)}</p>}
              {member.email && <p className="team-card__email">{member.email}</p>}

              <div className="team-card__actions">
                <button
                  className="btn-primary btn-whatsapp"
                  onClick={(e) => openWhatsApp(e, member.phone)}
                  title="Open WhatsApp"
                  aria-label={`Open WhatsApp for ${member?.name || 'member'}`}
                >
                  <FaWhatsapp />
                </button>
                <button
                  className="btn-primary btn-view-payments"
                  onClick={(e) => { e.stopPropagation(); openPaymentsForMember(member); }}
                  title={`View payments - ${member?.name || ''}`}
                  aria-label={`View payments for ${member?.name || 'member'}`}
                >
                  <FaReceipt />
                </button>
                <button
                  className="btn-primary btn-add-payment"
                  onClick={(e) => { e.stopPropagation(); openPaymentModal(member); }}
                  title="Add Payment"
                  aria-label={`Add payment for ${member?.name || 'member'}`}
                >
                  <FaPlusCircle />
                </button>
              </div>
            </div>
          ));
        })()}
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingMember ? t('team.editMember') : t('team.addMemberTitle')} size="small">
        <div className="team-form">
          <label className="team-form__label">{t('common.name')}</label>
          <input name="name" value={form.name} onChange={handleChange} className="team-form__input" />
          {errors.name && <div className="team-form__error">{errors.name}</div>}

          <label className="team-form__label">{t('common.phone')}</label>
          <input name="phone" type="tel" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} placeholder="10-digit mobile number" title="Phone: 10 digits, numbers only" value={form.phone} onChange={handleChange} className="team-form__input" />
          {errors.phone && <div className="team-form__error">{errors.phone}</div>}

          <label className="team-form__label">{t('common.email')}</label>
          <input name="email" value={form.email} onChange={handleChange} className="team-form__input" />
          {errors.email && <div className="team-form__error">{errors.email}</div>}

          <label className="team-form__label">{t('team.labelRole')}</label>
          <input name="role" value={form.role} onChange={handleChange} className="team-form__input" />
          {errors.role && <div className="team-form__error">{errors.role}</div>}



          <div className="modal-actions">
            <button className="btn-primary btn-cancel" onClick={closeModal}>Cancel</button>
            <button className="btn-primary" onClick={saveMember}>Save</button>
          </div>
        </div>
      </Modal>
      
      {/* Payment Modal */}
      <Modal 
        isOpen={isPaymentModalOpen} 
        onClose={closePaymentModal} 
        title={selectedMemberForPayment?.name || ''}
        size="small"
        headerActions={selectedMemberForPayment ? (
          <button type="button" className="btn-secondary btn-view-all-payments" onClick={() => { const member = selectedMemberForPayment; closePaymentModal(); setTimeout(() => openPaymentsForMember(member), 80); }} title="View all payments" aria-label="View all payments" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '6px 10px' }}><FaReceipt /> All Payments</button>
        ) : null}
      >
        <div className="team-form">
          <div className="team-form__label" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <span>Event</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={useCustomEventName}
                onChange={(e) => {
                  setUseCustomEventName(e.target.checked);
                  if (e.target.checked) {
                    setPaymentForm({ ...paymentForm, eventId: '' });
                    setPaymentErrors({ ...paymentErrors, eventId: '' });
                  } else {
                    setPaymentForm({ ...paymentForm, eventName: '' });
                    setPaymentErrors({ ...paymentErrors, eventName: '' });
                  }
                }}
              />
              Use custom event name
            </label>
          </div>
          {!useCustomEventName ? (
            <>
              <CustomSelect
                value={paymentForm.eventId}
                onChange={handlePaymentEventChange}
                placeholder={eventsForMember.length === 0 ? 'No assigned events' : 'Select Event'}
                className="team-form__input"
                disabled={eventsForMember.length === 0}
                options={
                  eventsForMember.length === 0
                    ? [{ value: '', label: 'No events assigned to this member' }]
                    : eventsForMember.map((event) => {
                        const eid = getTeamEventId(event);
                        return {
                          value: eid,
                          label: `${event.clientName || `Event ${event.quotationId || eid}`} (${eid}) - ${formatDate(event.services?.[0]?.date) || 'No date'}`
                        };
                      })
                }
              />
              {paymentErrors.eventId && <div className="team-form__error">{paymentErrors.eventId}</div>}
            </>
          ) : (
            <>
              <input 
                type="text"
                name="eventName" 
                value={paymentForm.eventName} 
                onChange={handlePaymentChange} 
                className="team-form__input"
                placeholder="Enter event name"
              />
              {paymentErrors.eventName && <div className="team-form__error">{paymentErrors.eventName}</div>}
            </>
          )}

          <div className="team-form__row team-form__row--2col">
            <div className="team-form__col">
              <label className="team-form__label">Amount (₹)</label>
              <input
                type="decimal"
                name="amount"
                value={paymentForm.amount}
                onChange={handlePaymentChange}
                className="team-form__input"
                placeholder="0"
                step="1"
                min="0"
              />
              {paymentErrors.amount && <div className="team-form__error">{paymentErrors.amount}</div>}
            </div>
            <div className="team-form__col">
              <label className="team-form__label">Payment Date</label>
              <input
                type="date"
                name="date"
                value={paymentForm.date}
                onChange={handlePaymentChange}
                className="team-form__input"
              />
              {paymentErrors.date && <div className="team-form__error">{paymentErrors.date}</div>}
            </div>
          </div>

          <label className="team-form__label">Status</label>
          <CustomSelect
            value={paymentForm.status}
            onChange={handlePaymentStatusChange}
            className="team-form__input"
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'partial', label: 'Partial' },
              { value: 'paid', label: 'Paid' }
            ]}
          />

          <label className="team-form__label">Notes (Optional)</label>
          <textarea 
            name="notes" 
            value={paymentForm.notes} 
            onChange={handlePaymentChange} 
            className="team-form__input"
            rows="3"
            placeholder="Add any notes about this payment..."
          />

          <div className="modal-actions">
            <button className="btn-primary" onClick={savePayment}>{editingPaymentId ? 'Update Payment' : 'Add Payment'}</button>
            <button className="btn-primary btn-cancel" onClick={closePaymentModal}>Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Payments Viewer Modal (table) */}
      <Modal
        isOpen={isPaymentsViewOpen}
        onClose={closePaymentsViewer}
        title={paymentsViewerMember?.name || ''}
        headerActions={(
          <>
            <button
              type="button"
              className="btn-secondary btn-export"
              onClick={exportPaymentsForMember}
              title="Export payments"
              aria-label="Export payments"
            >
              <FaFileExport /> Export
            </button>
            <button
              type="button"
              className="btn-secondary btn-add-payment"
              onClick={() => openPaymentModal(paymentsViewerMember)}
              title="Add payment"
              aria-label="Add payment"
            >
              <FaPlusCircle /> Add
            </button>
          </>
        )}
        size="large"
      >
        <div className="payments-viewer">
          <div className="payments-viewer__header">
            <div className="payments-viewer__stat">
              <div className="label">Total</div>
              <div className="value">₹{Math.round(getTotalPayments(paymentsViewerMember))}</div>
            </div>
            <div className="payments-viewer__stat">
              <div className="label">Paid</div>
              <div className="value paid">₹{Math.round(getPaidAmount(paymentsViewerMember))}</div>
            </div>
            <div className="payments-viewer__stat">
              <div className="label">Pending</div>
              <div className="value pending">₹{Math.round(getPendingAmount(paymentsViewerMember))}</div>
            </div>
          </div>
          <div className="payments-viewer__search">
            <input
              type="search"
              placeholder="Search by anything..."
              value={paymentsSearch}
              onChange={handlePaymentsSearch}
            />
          </div>
          <div className="payments-table">
          {paymentsViewerMember && (!paymentsViewerMember.payments || paymentsViewerMember.payments.length === 0) && (
            <p>No payments recorded for this member.</p>
          )}

          {paymentsViewerMember && paymentsViewerMember.payments && paymentsViewerMember.payments.length > 0 && (
            <>
              <div className="payments-table-scrollable">
                <table>
                  <thead>
                    <tr>
                      <th>Event ID</th>
                      <th>Event</th>
                      <th>Payment</th>
                      <th>Payment Date</th>
                      <th>Status</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const q = (paymentsSearch || '').trim().toLowerCase();
                      const allPayments = paymentsViewerMember.payments.slice().reverse().filter((p) => {
                        if (!q) return true;
                        const eventId = String(p.eventId || '').toLowerCase();
                        const eventName = String(p.eventName || '').toLowerCase();
                        const amount = String(p.amount || '').toLowerCase();
                        const dateStr = String(formatDate(p.date)).toLowerCase();
                        return (
                          eventId.includes(q) ||
                          eventName.includes(q) ||
                          amount.includes(q) ||
                          dateStr.includes(q)
                        );
                      });
                      const totalPages = Math.ceil(allPayments.length / itemsPerPage);
                      const paginatedPayments = allPayments.slice((paymentsPage - 1) * itemsPerPage, paymentsPage * itemsPerPage);
                      return paginatedPayments.map((p, idx) => {
                        // displayEventId: prefer stored payment.eventId, otherwise try to resolve from events list
                        const matchedEvent = events.find(ev => String(ev.id) === String(p.eventId));
                        const displayEventId = p.eventId || (matchedEvent ? matchedEvent.id : '—');
                        const displayEventName = p.eventName || (matchedEvent ? (matchedEvent.clientName || `Event ${matchedEvent.quotationId || matchedEvent.id}`) : 'Unnamed Event');
                        const rowKey = p.id || `tmp-${paymentsViewerMember._id || 'm'}-${idx}-${String(p.eventId||'').slice(0,6)}-${String(p.date||'').slice(0,10)}`;
                        return (
                            <tr key={rowKey}>
                              <td>{highlightText(displayEventId, paymentsSearch)}</td>
                              <td>{highlightText(displayEventName, paymentsSearch)}</td>
                              <td>₹{highlightText(Math.round(p.amount || 0), paymentsSearch)}</td>
                              <td>{highlightText(formatDate(p.date), paymentsSearch)}</td>
                              <td className={`payment-status status-${p.status}`}>{p.status}</td>
                              <td title={p.notes || ''}>{highlightText((p.notes || '').slice(0, 10) + ((p.notes || '').length > 10 ? '...' : ''), paymentsSearch)}</td>
                              <td>
                                <div className="payments-viewer__actions">
                                  {p.status !== 'paid' && (
                                    <button className="payments-viewer__action-btn btn-status-change" data-tooltip="Mark Paid" aria-label="Mark Paid" onClick={async () => {
                                      const ok = await confirm('Mark this payment as paid?');
                                      if (ok) updatePaymentStatus(paymentsViewerMember._id, p.id, 'paid');
                                    }}><FaCheckCircle /></button>
                                  )}
                                  <button className="payments-viewer__action-btn btn-edit-payment" data-tooltip="Edit" aria-label="Edit" onClick={() => openEditPaymentModal(paymentsViewerMember, p)}><FaEdit /></button>
                                  <button className="payments-viewer__action-btn btn-delete-payment" data-tooltip="Delete" aria-label="Delete" onClick={() => deletePayment(paymentsViewerMember._id, p.id)}><FaTrash /></button>
                                </div>
                              </td>
                            </tr>
                          );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              {(() => {
                const q = (paymentsSearch || '').trim().toLowerCase();
                const allPayments = paymentsViewerMember.payments.slice().reverse().filter((p) => {
                  if (!q) return true;
                  const eventId = String(p.eventId || '').toLowerCase();
                  const eventName = String(p.eventName || '').toLowerCase();
                  const amount = String(p.amount || '').toLowerCase();
                  const dateStr = String(formatDate(p.date)).toLowerCase();
                  return (
                    eventId.includes(q) ||
                    eventName.includes(q) ||
                    amount.includes(q) ||
                    dateStr.includes(q)
                  );
                });
                const totalPages = Math.ceil(allPayments.length / itemsPerPage);
                if (totalPages > 1) {
                  return (
                    <div className="pagination-controls">
                      <button 
                        onClick={() => setPaymentsPage(Math.max(1, paymentsPage - 1))} 
                        disabled={paymentsPage === 1}
                      >
                        Previous
                      </button>
                      <span>Page {paymentsPage} of {totalPages}</span>
                      <button 
                        onClick={() => setPaymentsPage(Math.min(totalPages, paymentsPage + 1))} 
                        disabled={paymentsPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
            </>
          )}
          </div>
        </div>
      </Modal>

      {/* Welcome invite for non-Mivent members */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => { setShowInviteModal(false); setInviteMember(null); }}
        title={inviteMember ? `Invite ${inviteMember.name}` : "Invite to Mivent"}
        size="medium"
      >
        {inviteMember && (() => {
          const waText = buildTeamInviteWhatsApp({ memberName: inviteMember.name, memberRole: inviteMember.role, ownerName: currentUserName });
          const emailData = buildTeamInviteEmail({ memberName: inviteMember.name, memberRole: inviteMember.role, ownerName: currentUserName, ownerEmail: currentUserEmail, ownerPhone: currentUserPhone });
          const handleWA = () => {
            const phone = String(inviteMember.phone || "").replace(/\D/g, "");
            const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(waText)}` : `https://wa.me/?text=${encodeURIComponent(waText)}`;
            window.open(url, "_blank");
          };
          const handleEmail = () => {
            if (!inviteMember.email) { showToast(`No email for ${inviteMember.name}`, { type: "warning" }); return; }
            window.location.href = `mailto:${encodeURIComponent(inviteMember.email)}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.body)}`;
          };
          const handleCopy = async (which) => {
            const txt = which === "email" ? `Subject: ${emailData.subject}\n\n${emailData.body}` : waText;
            try { await navigator.clipboard.writeText(txt); showToast("Invite copied", { type: "success" }); } catch { showToast(txt, { duration: 6000 }); }
          };
          return (
            <div className="share-modal">
              <p className="share-modal__lead">
                <strong>{inviteMember.name}</strong> is <strong>not on Mivent</strong> yet. Send this welcome invite via <strong>WhatsApp</strong> or <strong>Email</strong> — it explains what Mivent is and what happens next in simple words.
              </p>
              <div className="share-modal__actions">
                <button className="btn-primary" onClick={handleWA}><FaWhatsapp /> Send WhatsApp</button>
                <button className="btn-secondary" onClick={handleEmail} disabled={!inviteMember.email}>Email invite</button>
              </div>
              <details className="share-modal__preview" open>
                <summary>Preview messages</summary>
                <div className="share-modal__preview-body">
                  <div>
                    <div className="share-modal__preview-row">
                      <strong>WhatsApp</strong>
                      <button className="btn-secondary" onClick={() => handleCopy("whatsapp")}>Copy</button>
                    </div>
                    <pre>{waText}</pre>
                  </div>
                  <div className="share-modal__preview-divider">
                    <div className="share-modal__preview-row">
                      <strong>Email — {emailData.subject}</strong>
                      <button className="btn-secondary" onClick={() => handleCopy("email")}>Copy</button>
                    </div>
                    <pre>{emailData.body}</pre>
                  </div>
                </div>
              </details>
              <div className="share-modal__footer">
                <button className="btn-secondary" onClick={() => { setShowInviteModal(false); setInviteMember(null); }}>Done</button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default Team;
